import React, { useEffect } from 'react';

import Catalog from 'files/Catalog.js';
import FAQ from 'views/site/FAQ.js';
import Guarantee from 'views/site/Guarantee.js';
import Icon from 'views/site/Icon.js';
import QuizCTA from 'views/site/QuizCTA.js';
import Section, { SectionHeader } from 'views/site/Section.js';
import Testimonials from 'views/site/Testimonials.js';
import Theme from 'styles/Theme.js';
import TrustBar from 'views/site/TrustBar.js';
import Utils from 'files/Utils.js';

/*
 * SafetyCheck
 *
 * The landing page for the Nextdoor service ads. Cold traffic, arriving on a
 * phone, from a neighbourhood feed rather than a search — so the page has one
 * job and one action: start the sixty second check, which ends in the lead
 * form, which is what actually books the visit.
 *
 * MESSAGE MATCH IS THE WHOLE DESIGN. A visitor who taps an ad and lands on
 * different words bounces before reading the second line, and mismatch is the
 * single largest source of paid bounce. So the ad's own headline is the H1:
 * `?v=alarms` and `?v=rooms` carry the two creatives, and each one restates its
 * ad verbatim before saying anything new. Adding a third ad means adding a
 * third entry to VARIANTS and nothing else.
 *
 * There is NO neighbourhood personalisation on this page, and that is a finding
 * rather than an omission. Nextdoor's `{{neighborhood}}` and `{{city}}` macros
 * expand in the ad copy only — its creative specs put them there and nowhere
 * else — and the single parameter Nextdoor appends on click is `ndclid`, an
 * opaque attribution id carrying no geography. Neither the Ads API nor the
 * Conversions API reads a visitor's location back out; they manage campaigns and
 * receive conversions. So the page names the dealership's own city, which it
 * already knows, and leaves the neighbourly line to the ad, which is the only
 * surface that can actually write one.
 *
 * The site navigation stays. Landing page orthodoxy says strip it, and that is
 * right for a checkout funnel where every exit is a lost sale. It is wrong
 * here: this ad asks a stranger to let a person into their house, the single
 * most trust-gated thing on this site, and someone who wants to check that the
 * company is real should be able to. Denying that reads as something to hide.
 */

const VARIANTS = {

    // "Not all smoke alarms are equal. Get your free local safety check"
    alarms: {
        heading: 'Not all smoke alarms are equal',
        subtitle: 'The alarm that came with your house is built to the legal minimum: one sensor type, and slow to a smoldering fire. A certified safety advisor will come out and show you exactly where the gaps are.'
    },

    // "The rooms your alarms don't cover"
    rooms: {
        heading: "The rooms your alarms don't cover",
        subtitle: "Most homeowners get this one wrong: what protects your garage from a fire? You are not supposed to put a standard smoke alarm in there. We will inspect the rooms your current alarms don't."
    }
};

const DEFAULT_VARIANT = 'rooms';

// The glyph box and its gap. Fixed rather than derived, because Font Awesome
// marks are not square — sizing the box and not the mark is what keeps a column
// of headings and paragraphs sharing one left edge.
const ICON_SIZE = 20;
const ICON_GUTTER = 12;

/*
 * The four rooms, and why each is empty. This is the argument the ad opened,
 * finished — and it is deliberately about where a smoke alarm is not ALLOWED
 * rather than where one would fail, because the first is a fact a homeowner can
 * check and the second sounds like a sales line.
 */
const ROOMS = [{
    copy: 'Many house fires start here. It is also the one room where an alarm within five feet of the stove would go off every time you sear something, so code keeps it out and most builders leave the room empty.',
    icon: 'egg-fried',
    title: 'The kitchen'
},{
    copy: 'Exhaust, dust and the swing between a January night and a July afternoon mean a standard smoke alarm is not rated for a garage at all. Nothing else usually goes in either.',
    icon: 'building',
    title: 'The garage'
},{
    copy: 'Unconditioned, dusty, and where a good deal of wiring lives. A smoke alarm cannot be installed there, so a fire in an attic burns unannounced until it reaches a room that has one.',
    icon: 'house',
    title: 'The attic'
},{
    copy: 'Lint is highly flammable and it collects where nobody looks, behind a machine that runs while the house is asleep or out. Steam from the same room would nuisance-trip an alarm every week.',
    icon: 'basket2',
    title: 'The laundry room'
}];

/*
 * The two hazards that have nothing to do with which room you are in, because
 * no smoke alarm anywhere in the house detects either one. Water gets equal
 * billing with carbon monoxide deliberately: it is the claim homeowners
 * actually make, and a page that mentions it in a subordinate clause reads as
 * a fire company that also sells a leak puck.
 *
 * Every number here is already on this site, in Catalog.js, and came from the
 * in-home presentation. Nothing is rounded up for the landing page.
 */
const HAZARDS = [{
    copy: 'It has no smell and no color, and it comes off the furnace, the water heater, a gas range, or a car left running in an attached garage. It starts as a headache and nausea that reads like flu. By the time it is confusion and drowsiness, people are too disoriented to get themselves out.',
    icon: 'cloud-haze2-fill',
    points: [
        'Nothing in a smoke alarm detects it',
        'Needs its own sensor, in the rooms it collects in'
    ],
    stat: 'No smell, no warning',
    title: 'Carbon monoxide'
},{
    copy: 'Nobody is hurt and no alarm sounds, which is exactly why it costs so much: a supply line lets go behind a machine and the first anyone knows is a stain on a ceiling weeks later. The same sensor watches the temperature, so a pipe about to freeze says so before it bursts rather than after.',
    icon: 'droplet-fill',
    points: [
        '1 in 67 insured homes files a water or freeze claim every year',
        'Around a quarter of all home loss dollars are water and freezing'
    ],
    stat: '$15,400 average water damage claim',
    title: 'Water'
}];

/*
 * The night. Almost every fatal house fire happens while the household is
 * asleep, which makes waking up the entire problem — a detector that sounds in
 * a house nobody wakes up in has not done anything. Three cards, because the
 * answer genuinely is three separate pieces of hardware doing three jobs.
 */
const NIGHT = [{
    copy: 'A 520 Hz low-frequency horn, which is the sound proven most effective at waking people, paired with a shaker unit that sits under the mattress. It matters most for children, for adults over 40, and for anyone hard of hearing — the three groups a standard alarm most often fails to wake.',
    icon: 'moon-stars-fill',
    title: 'A sound built to wake people'
},{
    copy: 'A standard alarm tells you there is a fire. It does not tell you where, and every alarm in the house sounds at once. The bedside unit names the room, so the first decision of the night is made with information rather than in the dark.',
    icon: 'geo-alt-fill',
    title: 'It tells you which room'
},{
    copy: 'Text, email and push the instant a sensor fires, to you and to as many as eight people you choose. It works the same whether you are downstairs or a thousand miles away, and there is no subscription or monitoring fee attached to any of it.',
    icon: 'phone-fill',
    title: 'And it reaches your phone'
}];

/*
 * What the visit actually is, in the order it happens. Three steps, because a
 * fourth reads as a process rather than as an hour of somebody's evening.
 *
 * This has to describe a sales visit accurately. The advisor finds the gaps and
 * then shows the equipment that closes them, with prices — that is the point of
 * the visit and it is how the company earns. Writing it as a no-strings favour
 * would be a lie the visitor discovers at their own kitchen table, which is a
 * worse place to find it out than this page. Saying it plainly also reads as
 * more credible to an audience whose entire objection is that there is a catch.
 */
const STEPS = [{
    copy: 'Five questions about your home, answered from where you are sitting. At the end you get the list of rooms and hazards nothing in your house is covering.',
    title: 'Take the sixty second check'
},{
    copy: 'A certified safety advisor comes to you, walks the house room by room, and points at the gaps rather than describing them. Bring your questions.',
    title: 'An advisor walks it with you'
},{
    copy: 'They show you which sensors close each gap and what that would cost, so you are deciding against a real number instead of a guess. Going ahead is optional, and the list is yours either way.',
    title: 'You see what would close them'
}];

// The objections this offer raises, which are not the objections the product
// raises. Nobody arriving from an ad is asking about radio frequencies; they
// are asking what the catch is.
const getQuestions = dealerName => [{
    answer: 'The visit costs nothing and there is no obligation to buy at the end of it. Be clear on what it is, though: the advisor finds the gaps and then shows you the equipment that closes them, with prices, because that is how the company earns its living. The list of what your home is missing is yours to keep either way.',
    question: 'Is the inspection really free?'
},{
    answer: 'The advisor walks each room, checks what is already installed against what that room actually needs, and answers questions as they go, so it depends on the size of the house and how much you want to get into. They will give you a realistic idea of the time when the visit is arranged.',
    question: 'How long does it take?'
},{
    answer: `A trained safety advisor from ${dealerName}. The visit is arranged with you by phone first rather than turning up unannounced, so you agree the day and the time before anyone comes out.`,
    question: 'Who comes to my house?'
},{
    answer: 'You will be shown a solution, and it would waste your evening to pretend otherwise. What you should not get is pressure. The advisor walks the house, shows you what is missing and what covers it, and answers what you ask. If the answer is no, or not yet, that is a normal end to the visit — and if your home is already well covered, that is what you will be told.',
    question: 'Am I going to be sold to?'
},{
    answer: 'No. The check is five multiple choice questions about your home and takes about a minute. You only enter your details at the end, and only if you want the free inspection.',
    question: 'Do I have to give my details to see the results?'
}];

const SafetyCheck = ({ dealership, onOpenBlindSpots, params, utils }) => {

    const mobile = Utils.isMobile();

    // the devices aren't photographed for a dealership that hides the
    // products - see Catalog.isVisible
    const showProducts = Catalog.isVisible(dealership);

    let variant = VARIANTS[params?.v] || VARIANTS[DEFAULT_VARIANT];

    // The dealership's own city, from the Google-shaped address the record
    // already carries. For a local dealer that is the visitor's city nearly
    // every time, and when the record has no address the line simply reads
    // "local" instead — no request, no lookup, nothing to be wrong about.
    let place = dealership?.address?.locality || null;
    let dealerName = dealership?.name || 'your local dealer';

    /*
     * The Open Graph tags are written by index.php from the `route` parameter
     * and are not ours to set from here, but the tab title and the description
     * a search engine reads are, and a landing page carrying the site's generic
     * title is a wasted signal.
     */
    useEffect(() => {

        let previous = document.title;
        document.title = `${dealership.name}: ${variant.heading} | ${dealerName}`;

        let description = document.querySelector('meta[name="description"]');
        let restore = description?.getAttribute('content');
        description?.setAttribute('content', `Free in-home safety inspection from ${dealerName}. Take the sixty second check to see the rooms and hazards your current alarms do not cover.`);

        return () => {
            document.title = previous;
            if(description && restore) {
                description.setAttribute('content', restore);
            }
        };
    }, [dealerName, variant.heading]);

    /*
     * The hero read as "another section" for two measurable reasons.
     *
     * One: sectionHeading is 44px on desktop and the headline was 46. Two
     * pixels is not a hierarchy, so the first line of the page carried the same
     * weight as the seven section headings under it.
     *
     * Two: a centered eyebrow over a heading over a subtitle IS SectionHeader.
     * The hero was not merely similar to the sections below it, it was built
     * from the same three parts in the same order.
     *
     * Alignment is NOT the lever — the site is centered throughout and a
     * left-aligned hero reads as a different website rather than a different
     * kind of block. So the difference is made in the other three places:
     *
     *   Size. 58 against 44, with tracking pulled in to -0.035em, because
     *   letters read too far apart as display type grows. Size, leading and
     *   tracking move as one set rather than size alone.
     *
     *   The eyebrow becomes a badge. A bare uppercase label is the one part
     *   that made this a SectionHeader; a pill is a hero device, and it also
     *   merchandises the word "free" instead of whispering it.
     *
     *   The photograph carries its own warm grey backdrop, so it needs no stage
     *   behind it — a radius and the card shadow sit it on the page as a plate
     *   rather than floating it on white.
     */
    const getHero = () => (
        <section style={{
            backgroundColor: Theme.colors.white,
            paddingTop: Theme.layout.headerOffset(),
            width: '100%'
        }}>
            <div style={{
                margin: '0 auto',
                maxWidth: Theme.layout.maxWidth,
                padding: mobile ? '28px 20px 48px' : '56px 40px 72px',
                textAlign: 'center'
            }}>
                {/* The offer, as a badge rather than a section eyebrow. Tinted
                    rather than filled so it cannot compete with the button. */}
                <span style={{
                    ...Theme.textStyles.label({ color: Theme.colors.primary() }),
                    backgroundColor: Theme.tint(0.08),
                    border: `1px solid ${Theme.tint(0.14)}`,
                    borderRadius: 999,
                    display: 'inline-block',
                    marginBottom: mobile ? 18 : 22,
                    padding: '7px 15px'
                }}>
                    {place ? `Free safety check · ${place}` : 'Free local safety check'}
                </span>

                {/* The ad's own headline, verbatim. See the note on message
                    match above — this is the line the visitor just tapped. */}
                <h1 style={{
                    ...Theme.textStyles.heading({ fontSize: mobile ? 34 : 58 }),
                    letterSpacing: mobile ? '-0.03em' : '-0.035em',
                    lineHeight: mobile ? 1.08 : 1.03,
                    margin: '0 auto',
                    marginBottom: mobile ? 16 : 22,
                    maxWidth: '17ch',
                    textAlign: 'center',
                    textWrap: 'balance'
                }}>{variant.heading}</h1>

                <p style={{
                    ...Theme.textStyles.body({ color: Theme.colors.subText(), fontSize: mobile ? 17 : 20, lineHeight: 1.5 }),
                    margin: '0 auto',
                    maxWidth: '52ch',
                    textAlign: 'center'
                }}>{variant.subtitle}</p>

                {/* Button and risk reversal are one unit. The reassurance has to
                    land in the same glance as the tap, not a paragraph away. */}
                <div style={{ marginTop: mobile ? 28 : 34 }}>
                    <button
                    className={'pressable'}
                    onClick={onOpenBlindSpots}
                    style={{
                        ...Theme.styles.button.primary(),
                        fontSize: 17,
                        padding: '16px 34px',
                        ...mobile && { width: '100%' }
                    }}>
                        {'Start the 60-second check'}
                    </button>

                    <p style={{
                        ...Theme.textStyles.body({ color: Theme.colors.subText(), fontSize: 14, textAlign: 'center' }),
                        marginBottom: 0,
                        marginTop: 12
                    }}>
                        {'No cost and no obligation. About a minute.'}
                    </p>
                </div>

                {showProducts && (
                    <div style={{
                        borderRadius: mobile ? 20 : 28,
                        boxShadow: Theme.boxShadow.card,
                        marginTop: mobile ? 36 : 56,
                        overflow: 'hidden',
                        width: '100%'
                    }}>
                        <img
                        alt={'The smoke, heat, carbon monoxide, water and bedside units that make up the network'}
                        src={'images/network-sensors-collection-white.jpg'}
                        style={{
                            display: 'block',
                            height: 'auto',
                            width: '100%'
                        }} />
                    </div>
                )}
            </div>
        </section>
    );

    /*
     * A ruled list, not a card grid. Four sections of this page were the same
     * unit — a centered header over a grid of icon cards — and four identical
     * blocks in a row is what makes a landing page skimmable in the bad sense.
     *
     * The form is borrowed from the storefront's own hairline-divided panel
     * rather than invented, so the page gains rhythm without gaining a new
     * visual language. It also suits the content better: this is a list of
     * rooms, and a list looks like a list.
     */
    const getRooms = () => (
        <Section id={'safety-check-rooms'}>
            <SectionHeader
            eyebrow={'Where the gaps are'}
            subtitle={'These are not rooms somebody forgot. They are rooms a standard smoke alarm is not permitted in, because the steam, dust and exhaust in them would set it off every week. Code keeps it out, and most homes end up with nothing there at all.'}
            title={'Four rooms a smoke alarm cannot sit in'} />

            <div style={{
                ...Theme.styles.card(),
                boxShadow: Theme.boxShadow.card,
                padding: mobile ? '4px 22px' : '8px 36px'
            }}>
                {ROOMS.map((room, index) => (
                    <div
                    key={room.title}
                    style={{
                        alignItems: mobile ? 'flex-start' : 'center',
                        borderTop: index === 0 ? 'none' : `1px solid ${Theme.colors.border()}`,
                        display: 'flex',
                        flexDirection: mobile ? 'column' : 'row',
                        gap: mobile ? 8 : 32,
                        padding: mobile ? '22px 0' : '26px 0'
                    }}>
                        {/* Fixed column so the room names stack into a readable
                            edge rather than a ragged one. */}
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexShrink: 0,
                            gap: 12,
                            width: mobile ? 'auto' : 210
                        }}>
                            <Icon name={room.icon} size={17} color={Theme.colors.primary()} style={{ flexShrink: 0 }} />
                            <span style={Theme.textStyles.subHeading({ fontSize: mobile ? 17 : 19 })}>{room.title}</span>
                        </div>
                        <span style={{
                            ...Theme.textStyles.body({ color: Theme.colors.subText(), fontSize: mobile ? 14 : 15 }),
                            display: 'block'
                        }}>{room.copy}</span>
                    </div>
                ))}
            </div>
        </Section>
    );

    const getHazards = () => (
        <Section id={'safety-check-hazards'} tinted={true}>
            <SectionHeader
            eyebrow={'Not a fire problem'}
            subtitle={'Rooms are only half of it. These two have nothing to do with where you stand in the house, because there is no smoke alarm made that detects either one of them — not in the hallway, not anywhere.'}
            title={'Two things no smoke alarm can hear'} />

            <div style={{
                display: 'grid',
                gap: mobile ? 16 : 20,
                gridTemplateColumns: mobile ? '1fr' : 'repeat(2, 1fr)'
            }}>
                {HAZARDS.map(hazard => (
                    <div
                    key={hazard.title}
                    style={{
                        ...Theme.styles.card(),
                        boxShadow: Theme.boxShadow.card,
                        display: 'flex',
                        flexDirection: 'column',
                        padding: mobile ? 24 : 30,
                        textAlign: 'left'
                    }}>
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 12,
                            marginBottom: 12
                        }}>
                            <Icon name={hazard.icon} size={19} color={Theme.colors.primary()} style={{ flexShrink: 0 }} />
                            <span style={Theme.textStyles.subHeading({ fontSize: 20 })}>{hazard.title}</span>
                        </div>

                        <span style={{
                            ...Theme.textStyles.body({ fontSize: 14 }),
                            display: 'block',
                            marginBottom: 16
                        }}>{hazard.copy}</span>

                        <div style={{
                            borderTop: `1px solid ${Theme.colors.border()}`,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            marginTop: 'auto',
                            paddingTop: 16
                        }}>
                            {hazard.points.map(point => (
                                <div
                                key={point}
                                style={{ alignItems: 'center', display: 'flex', gap: 10 }}>
                                    <Icon name={'warning'} size={12} color={Theme.colors.primary()} style={{ flexShrink: 0 }} />
                                    <span style={Theme.textStyles.body({ color: Theme.colors.subText(), fontSize: 14 })}>{point}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Cited, and linked, because a number a visitor cannot check is
                worth less than no number at all. Same source the storefront
                uses for the same figures. */}
            <p style={{
                ...Theme.textStyles.caption({ color: Theme.colors.lightGrey, textAlign: 'center' }),
                marginTop: 24
            }}>
                {'Water figures: '}
                <a
                href={'https://www.iii.org/fact-statistic/facts-statistics-homeowners-and-renters-insurance'}
                rel={'noopener noreferrer'}
                style={{ color: Theme.colors.primary(), fontWeight: 500, textDecoration: 'none' }}
                target={'_blank'}>
                    {'Insurance Information Institute'}
                </a>
                {' analysis of ISO homeowners insurance data, 2019–2023 averages.'}
            </p>
        </Section>
    );

    /*
     * The one section on this page with a photograph in it, because it is the
     * one part that is about a physical object sitting on a nightstand rather
     * than about an absence. The header stays centered — the site is centered
     * and a split is a layout, not a licence to re-align the page.
     */
    const getNight = () => (
        <Section id={'safety-check-night'}>
            <SectionHeader
            eyebrow={'The part nobody plans for'}
            subtitle={'Almost every fatal house fire happens while the household is asleep. Which means detecting it is the easy half — a sensor that sounds in a house nobody wakes up in has not actually done anything.'}
            title={'It happens at three in the morning'} />

            <div style={{
                alignItems: 'center',
                display: 'grid',
                gap: mobile ? 28 : 56,
                gridTemplateColumns: mobile ? '1fr' : '0.85fr 1fr'
            }}>
                <div style={{
                    backgroundColor: Theme.colors.white,
                    border: `1px solid ${Theme.colors.border()}`,
                    borderRadius: mobile ? 20 : 24,
                    boxShadow: Theme.boxShadow.card,
                    overflow: 'hidden'
                }}>
                    {/* This half of the layout can't be left empty, so a
                        dealership that hides the products gets a plain
                        placeholder the same shape until there's a photo that
                        doesn't show the devices. */}
                    {showProducts ? (
                        <img
                        alt={'The bedside unit and the under-mattress shaker'}
                        src={'images/omnishield-bed-shaker.jpg'}
                        style={{ display: 'block', height: 'auto', width: '100%' }} />
                    ) : (
                        <div
                        aria-hidden={true}
                        style={{
                            alignItems: 'center',
                            aspectRatio: '1 / 1',
                            backgroundColor: Theme.colors.offWhite,
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%'
                        }}>
                            <Icon color={Theme.colors.lightGrey} name={'moon-stars-fill'} size={48} />
                        </div>
                    )}
                </div>

                {/*
                    Three faults were visible here and all three are alignment
                    rather than content.
                  *
                  * The icon sat in a stretched flex row, so it centered itself
                  * against the height of the whole item and floated beside the
                  * middle of the paragraph rather than beside the heading it
                  * belongs to. It now sits in the heading row, where proximity
                  * says what it is for.
                  *
                  * The copy then has to be indented by exactly the icon plus
                  * its gap, so the heading and the paragraph share a left edge.
                  * An icon that pushes only the title in is the misalignment
                  * that reads as carelessness.
                  *
                  * And the column had no boundary at all while the photograph
                  * opposite it had a hard one. Hairlines between the items give
                  * the text a right edge and a rhythm without adding a second
                  * card to compete with the image.
                */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {NIGHT.map((item, index) => (
                        <div
                        key={item.title}
                        style={{
                            borderTop: index === 0 ? 'none' : `1px solid ${Theme.colors.border()}`,
                            paddingBottom: index === NIGHT.length - 1 ? 0 : (mobile ? 20 : 24),
                            paddingTop: index === 0 ? 0 : (mobile ? 20 : 24)
                        }}>
                            <div style={{
                                alignItems: 'center',
                                display: 'flex',
                                gap: ICON_GUTTER,
                                marginBottom: 8
                            }}>
                                <span style={{
                                    alignItems: 'center',
                                    display: 'inline-flex',
                                    flexShrink: 0,
                                    height: ICON_SIZE,
                                    justifyContent: 'center',
                                    width: ICON_SIZE
                                }}>
                                    <Icon name={item.icon} size={17} color={Theme.colors.primary()} />
                                </span>
                                <span style={Theme.textStyles.subHeading({ fontSize: mobile ? 17 : 19 })}>{item.title}</span>
                            </div>

                            {/* Measure capped rather than left to the column, so
                                the lines stay near the length the eye tracks
                                comfortably instead of stretching to the edge. */}
                            <span style={{
                                ...Theme.textStyles.body({ color: Theme.colors.subText(), fontSize: mobile ? 14 : 15 }),
                                display: 'block',
                                marginLeft: mobile ? 0 : ICON_SIZE + ICON_GUTTER,
                                maxWidth: '58ch'
                            }}>{item.copy}</span>
                        </div>
                    ))}
                </div>
            </div>
        </Section>
    );

    const getSteps = () => (
        <Section id={'safety-check-steps'} tinted={true}>
            <SectionHeader
            eyebrow={'What happens next'}
            subtitle={`The visit costs nothing and buying is not a condition of it. It is not a charity call either: ${dealerName} finds what your home is missing and then shows you the equipment that covers it, prices included. Plenty of people take the list away and think about it.`}
            title={'What the free inspection actually is'} />

            <div style={{
                display: 'grid',
                gap: mobile ? 16 : 20,
                gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)'
            }}>
                {STEPS.map((step, index) => (
                    <div
                    key={step.title}
                    style={{
                        ...Theme.styles.card(),
                        boxShadow: Theme.boxShadow.card,
                        padding: mobile ? 24 : 28,
                        textAlign: 'left'
                    }}>
                        <div style={{
                            alignItems: 'center',
                            backgroundColor: Theme.colors.primaryFill(),
                            borderRadius: 15,
                            color: Theme.colors.onPrimary(),
                            display: 'flex',
                            fontSize: 14,
                            fontWeight: 700,
                            height: 30,
                            justifyContent: 'center',
                            marginBottom: 14,
                            width: 30
                        }}>{index + 1}</div>
                        <span style={{
                            ...Theme.textStyles.subHeading({ fontSize: 18 }),
                            display: 'block',
                            marginBottom: 8
                        }}>{step.title}</span>
                        <span style={{
                            ...Theme.textStyles.body({ fontSize: 14 }),
                            display: 'block'
                        }}>{step.copy}</span>
                    </div>
                ))}
            </div>
        </Section>
    );

    if(!dealership) {
        return null;
    }

    return (
        <div
        className={'site'}
        style={{
            backgroundColor: Theme.colors.white,
            width: '100%'
        }}>
            {getHero()}

            <TrustBar />

            {getRooms()}

            {getHazards()}

            {getNight()}

            {getSteps()}

            <QuizCTA
            dealership={dealership}
            heading={'See what your home is missing'}
            onOpenQuiz={onOpenBlindSpots}
            subtext={`Five questions, about a minute. At the end you will have the list for your own house, and ${dealerName} can come out and walk it with you, free.`} />

            <Guarantee />

            <FAQ
            dealership={dealership}
            items={getQuestions(dealerName)} />

            <Testimonials
            dealership={dealership}
            utils={utils} />

            <QuizCTA
            dealership={dealership}
            heading={'Ready to find out what yours is missing?'}
            onOpenQuiz={onOpenBlindSpots}
            subtext={'Start with the 60 second check. The visit that follows is free, and going ahead with anything is your decision.'} />
        </div>
    )
}

export default SafetyCheck;
